// ============================================================================
// FlightAware Hooks — TanStack Query
// ============================================================================
// Manual sync / import / enable for the FlightAware AeroAPI integration.

import { useMemo } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTenant } from '@/contexts'
import {
  syncFlightAware,
  importFlightAwareFlight,
  importFlightAwareFlightsBulk,
  setFlightAwareEnabled,
  type FlightAwareFlight,
  type FlightAwareSyncResult,
} from '@/lib/api/flightaware'
import { locationScheduleKeys } from '@/hooks/useLocationSchedules'
import { integrationKeys, useIntegrationOverview } from '@/hooks/useIntegrations'
import { broadcastInvalidation } from '@/lib/sync/broadcast-channel'

/** Default sync window — how far ahead AeroAPI arrivals/departures are fetched. */
export const FLIGHTAWARE_HORIZON_HOURS = 48

/**
 * FlightAware status for a location, derived from the integration overview:
 * whether it's enabled and the last successful sync time (persists across reloads).
 */
export function useFlightAwareStatus(locationId: string | null): {
  enabled: boolean
  lastSync: string | null
} {
  const { data: overview } = useIntegrationOverview()
  return useMemo(() => {
    if (!locationId || !overview) return { enabled: false, lastSync: null }
    for (const set of overview) {
      if (set.provider !== 'flightaware' && set.integration_providers?.code !== 'flightaware') continue
      const cred = (set.integration_credentials ?? []).find(
        (c) => c.location_id === locationId && c.is_active,
      )
      if (cred) return { enabled: true, lastSync: cred.last_successful_sync ?? null }
    }
    return { enabled: false, lastSync: null }
  }, [overview, locationId])
}

/** True when FlightAware is enabled for the given location (per the overview). */
export function useFlightAwareEnabled(locationId: string | null): boolean {
  return useFlightAwareStatus(locationId).enabled
}

export function useFlightAwareSync() {
  const { currentOrganization } = useTenant()
  const queryClient = useQueryClient()

  return useMutation<FlightAwareSyncResult, Error, { locationId: string; horizonHours?: number }>({
    mutationFn: ({ locationId, horizonHours }) =>
      syncFlightAware(currentOrganization!.id, locationId, horizonHours),
    onSuccess: (result) => {
      // Enriched schedules changed → refresh the board.
      queryClient.invalidateQueries({ queryKey: locationScheduleKeys.all })
      broadcastInvalidation([locationScheduleKeys.all])
      const orgId = currentOrganization?.id
      if (orgId) {
        queryClient.invalidateQueries({ queryKey: integrationKeys.overview(orgId) })
      }
      const { matched, newAtAirport } = result.counts
      toast.success(`FlightAware synced`, {
        description: `${matched} schedule${matched === 1 ? '' : 's'} updated · ${newAtAirport} new flight${newAtAirport === 1 ? '' : 's'} at ${result.airportIcao}`,
      })
    },
    onError: (error) => {
      toast.error('FlightAware sync failed', { description: error.message })
    },
  })
}

export function useImportFlightAwareFlight() {
  const { currentOrganization } = useTenant()
  const queryClient = useQueryClient()

  return useMutation<
    { schedule_id: string; alreadyImported?: boolean },
    Error,
    { locationId: string; flight: FlightAwareFlight }
  >({
    mutationFn: ({ locationId, flight }) =>
      importFlightAwareFlight(currentOrganization!.id, locationId, flight),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: locationScheduleKeys.all })
      broadcastInvalidation([locationScheduleKeys.all])
      toast.success(result.alreadyImported ? 'Already on the board' : 'Flight imported to schedule')
    },
    onError: (error) => {
      toast.error('Import failed', { description: error.message })
    },
  })
}

export function useImportFlightAwareFlights() {
  const { currentOrganization } = useTenant()
  const queryClient = useQueryClient()

  return useMutation<
    { imported: number; alreadyImported: number; skipped: number; scheduleIds: string[] },
    Error,
    { locationId: string; flights: FlightAwareFlight[] }
  >({
    mutationFn: ({ locationId, flights }) =>
      importFlightAwareFlightsBulk(currentOrganization!.id, locationId, flights),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: locationScheduleKeys.all })
      broadcastInvalidation([locationScheduleKeys.all])
      const parts: string[] = []
      if (result.imported) parts.push(`${result.imported} imported`)
      if (result.alreadyImported) parts.push(`${result.alreadyImported} already on board`)
      if (result.skipped) parts.push(`${result.skipped} skipped`)
      toast.success('Imported flights', { description: parts.join(' · ') || 'Nothing to import' })
    },
    onError: (error) => {
      toast.error('Bulk import failed', { description: error.message })
    },
  })
}

export function useSetFlightAwareEnabled() {
  const { currentOrganization } = useTenant()
  const queryClient = useQueryClient()

  return useMutation<{ enabled: boolean }, Error, { locationId: string; enabled: boolean }>({
    mutationFn: ({ locationId, enabled }) =>
      setFlightAwareEnabled(currentOrganization!.id, locationId, enabled),
    onSuccess: (result) => {
      const orgId = currentOrganization?.id
      if (orgId) {
        queryClient.invalidateQueries({ queryKey: integrationKeys.overview(orgId) })
        queryClient.invalidateQueries({ queryKey: integrationKeys.credentials(orgId) })
        queryClient.invalidateQueries({ queryKey: integrationKeys.health(orgId) })
      }
      toast.success(result.enabled ? 'FlightAware enabled for this location' : 'FlightAware disabled')
    },
    onError: (error) => {
      toast.error('Failed to update FlightAware', { description: error.message })
    },
  })
}
